import useTranslation from 'next-translate/useTranslation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import { useCallback, useContext } from 'react';

import { cancelEntry } from '~/domains/contest/domains/services/entries.service.api';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { ConfirmModal } from '~/domains/common/components/Modal/Confirm/ConfirmModal';
import { ContestQueryKeys } from '~/domains/contest/constants/query';
import { UserContext } from '~/components/providers/UserProvider';

type EntryWithdrawModalProps = {
  entryId: string;
  isOpen: boolean;
  setModal: (boolean) => void;
  contestId: string;
  isEntertainmentOnly?: boolean;
};

export function EntryWithdrawModal({
  entryId,
  isOpen,
  setModal,
  contestId,
  isEntertainmentOnly,
}: EntryWithdrawModalProps) {
  const { t } = useTranslation('contest');
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();
  const { refetch: getUserBalance } = useWalletBalance();

  const handleCancelEntry = useCallback(async () => {
    await cancelEntry(entryId);
    await queryClient.refetchQueries(['contests', contestId]);
    await queryClient.refetchQueries(
      ContestQueryKeys.ENTRIES_FOR_CURRENT_USER({ contestId, userId: user?.id }),
    );
    await queryClient.refetchQueries(['entries']);
    await queryClient.refetchQueries(['myEntries']);
    await getUserBalance();
  }, [entryId, queryClient, contestId, getUserBalance]);

  const onClose = () => setModal(false);

  const { mutate: withdrawEntry, isLoading: isWithdrawingEntryLoading } = useMutation({
    mutationKey: ['cancelEntry', entryId],
    mutationFn: handleCancelEntry,
    onSuccess: () => {
      showNotification({
        message: t('detail.entries.entry.withdrawalModal.success.message'),
        color: 'green',
      });
    },
    onError: () => {
      showNotification({
        title: t('detail.entries.entry.withdrawalModal.error.title'),
        message: t('detail.entries.entry.withdrawalModal.error.message'),
        color: 'red',
      });
    },
    onSettled: onClose,
  });

  return (
    <ConfirmModal
      cancelText={t('detail.entries.entry.withdrawalModal.cancel')}
      confirmText={t('detail.entries.entry.withdrawalModal.withdraw')}
      description={isEntertainmentOnly
        ? t('detail.entries.entry.withdrawalModal.descriptionEO')
        : t('detail.entries.entry.withdrawalModal.description')}
      isOnConfirmLoading={isWithdrawingEntryLoading}
      isOpen={isOpen}
      onConfirm={withdrawEntry}
      onClose={onClose}
      title={t('detail.entries.entry.withdrawalModal.title')}
    />
  );
}
